<template>
  <LoaderComponent v-if="isLoading" />
  
  <div v-else class="main-content-wrapper">
    
    <div class="main-content-area">
      <ResumeConfig v-if="shouldShowResumeModal" @continue="handleResume" @cancel="handleCancelResume" />
      <router-view />
    </div>

    <FooterComponent v-if="!isConfiguratorRoute" />
  </div>
</template>

<script>
import { nextTick, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'

import { useTrailerStore } from './stores/trailerStore'
import { getConfigFromStorage, clearConfigStorage } from './services/configStorage'

import { useTextsStore } from '@/stores/textsStore'

const LoaderComponent = defineAsyncComponent(() => import('./components/common/LoaderComponent.vue'))
const ResumeConfig = defineAsyncComponent(() => import('./components/configurator-page/ResumeConfig.vue'))
const FooterComponent = defineAsyncComponent(() => import('./components/common/FooterComponent.vue'))

const excludedRoutes = [
  '/cms',
  '/about-us',
  '/terms-of-service',
  '/privacy-policy',
  '/404',
  '/order-confirmation'
]

export default {
  components: {
    LoaderComponent,
    ResumeConfig,
    FooterComponent,
  },
  data() {
    return {
      isLoading: true,
      showResumeModal: false,
      isExcluded: false,
    }
  },
  computed: {
    isConfiguratorRoute() {
      return this.$route.path.startsWith('/configurator')
    },
    shouldShowResumeModal() {
      return this.showResumeModal && !this.isExcluded
    },
  },
  async mounted() {
    const trailerStore = useTrailerStore()
    const router = useRouter()
    const textsStore = useTextsStore()

    textsStore.fetchTexts()

    await router.isReady()
    await nextTick()

    this.isExcluded = excludedRoutes.some(route =>
      this.$route.path === route || this.$route.path.startsWith(route + '/')
    )

    const fetchPromises = []

    if (!trailerStore.trailers.length) {
      fetchPromises.push(trailerStore.fetchTrailers())
    }

    if (!trailerStore.addonsForModels.length) {
      fetchPromises.push(trailerStore.fetchAddonsForModels())
    }

    if (!trailerStore.addons.length) {
      fetchPromises.push(trailerStore.fetchAddons())
    }

    await Promise.all(fetchPromises)

    const saved = getConfigFromStorage()
    if (saved && !trailerStore.selectedTrailer && !this.isExcluded) {
      this.showResumeModal = true
    }

    this.isLoading = false
  },
  methods: {
    handleResume() {
      this.showResumeModal = false
      this.$router.push('/configurator')
    },
    handleCancelResume() {
      clearConfigStorage()
      this.showResumeModal = false
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');

* {
  box-sizing: border-box;
}

html, body, #app {
    height: 100%;
}

body {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    color: $color-text;
    background-image: url('~@/assets/images/tlo_strony.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: center;
    margin: 0 auto;
    display: flex; 
    flex-direction: column; 
}

#app {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.main-content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.main-content-area {
    flex-grow: 1;
}

h1,
h2,
h3,
h4,
h5,
h6,
p {
  cursor: default;
}

footer {
  margin-top: -100px;}
</style>